import { useEffect } from "react";

/**
 * React hook that subscribes to browser lifecycle events related to the
 * back/forward cache (BFCache).
 *
 * BFCache allows browsers to preserve a complete page snapshot when a user
 * navigates away, enabling near-instant restoration when navigating back
 * or forward. This hook is invoked when the page is restored from BFCache.
 *
 * For more information about BFCache and the Page Lifecycle API:
 * https://developer.chrome.com/docs/web-platform/page-lifecycle-api#back-forward-cache
 *
 * @param onRestore - Callback invoked when the page is restored from BFCache.
 * @param onTeardown - Callback invoked when the page is hidding and entering BFCache.
 * @param options - Optional configuration for the hooks.
 *
 * @remarks
 * - BFCache behavior and support vary by browser.
 * - The callback is only invoked for BFCache restores and not for a normal
 *   page load.
 * - Use this callback to revalidate or refresh state that may have become
 *   stale while the page was cached.
 */
export function useBackForwardCache(
  onRestore: (event: Event) => void,
  onTeardown: (event: Event) => void,
  options: AddEventListenerOptions = {},
): void {
  useEffect(() => {
    window.addEventListener('pageshow', onRestore, options);
    window.addEventListener('pagehide', onTeardown, options);
    return () => {
        window.removeEventListener('pageshow', onRestore, options);
        window.removeEventListener('pagehide', onTeardown, options);
    };
  }, [onRestore, onTeardown, options]);
}